import { useEffect, useMemo, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { apiGet, apiPost } from "@/api/client";
import {
  FOUNDATION_PLAN,
  PLAN_ADDONS,
  PLAN_ONE_TIME,
  PlanAddonOffer,
  applyPublicPricing,
  normalizePlanAddons,
  normalizePlanOneTime,
  togglePlanSelection,
} from "@/components/project/OnboardingPlan";
import { formatMoney } from "@/lib/currency";

export function BillingAddonsPanel({ business, refresh, className = "", embedded = false }) {
  const ownedAddons = normalizePlanAddons(business?.subscription_addons);
  const ownedOneTime = normalizePlanOneTime(business?.subscription_one_time);
  const [draftAddons, setDraftAddons] = useState([]);
  const [draftOneTime, setDraftOneTime] = useState([]);
  const [paying, setPaying] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [billingConfig, setBillingConfig] = useState({
    stripe_configured: false,
    test_mode: false,
    live_mode: false,
  });

  useEffect(() => {
    apiGet("/billing/config")
      .then((config) => {
        applyPublicPricing(config.pricing);
        setBillingConfig(config);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") !== "1" || params.get("billing") !== "1") return;
    const sessionId = params.get("session_id");
    (async () => {
      try {
        await apiPost("/onboarding/subscription/confirm", { session_id: sessionId || undefined });
        await refresh?.();
        toast.success("Stripe add-on payment confirmed");
      } catch (error) {
        toast.error(error.response?.data?.detail?.error?.message || "Could not confirm Stripe payment");
      } finally {
        params.delete("paid");
        params.delete("session_id");
        const next = `${window.location.pathname}?${params.toString()}`.replace(/\?$/, "");
        window.history.replaceState({}, "", next);
      }
    })();
  }, [refresh]);

  const subscriptionActive = business?.subscription_status === "active";
  const monthlyDelta = useMemo(
    () => draftAddons.reduce((sum, id) => {
      const item = PLAN_ADDONS.find((addon) => addon.id === id);
      return sum + Number(item?.price || 0);
    }, 0),
    [draftAddons]
  );
  const oneTimeDelta = useMemo(
    () => draftOneTime.reduce((sum, id) => {
      const item = PLAN_ONE_TIME.find((entry) => entry.id === id);
      return sum + Number(item?.price || 0);
    }, 0),
    [draftOneTime]
  );
  const dueNow = monthlyDelta + oneTimeDelta;
  const stripeReady = Boolean(billingConfig.stripe_configured);
  const dueLabel = "Stripe Checkout";

  const purchase = async () => {
    if (!draftAddons.length && !draftOneTime.length) {
      toast.error("Select an add-on to purchase");
      return;
    }
    if (!stripeReady) {
      toast.error("Payments are temporarily unavailable. Please try again shortly.");
      return;
    }
    setPaying(true);
    try {
      const result = await apiPost("/subscription/addons/purchase", {
        addons: draftAddons,
        one_time: draftOneTime,
        payment_mode: "stripe",
      });
      if (result.checkout_url) {
        if (result.monthly_applied) {
          toast.success("Monthly add-ons added to your Stripe subscription. Continue to pay one-time items…");
        } else {
          toast.message("Redirecting to Stripe Checkout…");
        }
        window.location.assign(result.checkout_url);
        return;
      }
      setDraftAddons([]);
      setDraftOneTime([]);
      await refresh?.();
      toast.success(
        result.monthly_applied
          ? "Monthly add-ons added to your Stripe subscription"
          : "Add-on payment complete"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "Could not purchase add-ons");
    } finally {
      setPaying(false);
    }
  };

  const removeMonthly = async (id) => {
    setRemovingId(id);
    try {
      await apiPost("/subscription/addons/remove", { addons: [id] });
      await refresh?.();
      toast.success("Monthly add-on removed");
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "Could not remove add-on");
    } finally {
      setRemovingId(null);
    }
  };

  const body = (
      <div className={embedded ? "" : "mt-4"} data-testid="billing-addons-panel-content">
        <div className="rounded-xl bg-[#F8F5EE] p-4" data-testid="billing-foundation-summary">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-extrabold text-[#173B2F]">{FOUNDATION_PLAN.name}</p>
              <p className="text-xs text-muted-foreground">
                {subscriptionActive ? "Active subscription" : "Not active yet — finish onboarding payment to go live"}
              </p>
            </div>
            <p className="font-extrabold">
              {formatMoney(FOUNDATION_PLAN.price, FOUNDATION_PLAN.currency)}
              <span className="text-xs font-bold text-muted-foreground">/mo + VAT</span>
            </p>
          </div>
          {(ownedAddons.length > 0 || ownedOneTime.length > 0) && (
            <ul className="mt-3 space-y-2 text-sm" data-testid="billing-owned-addons">
              {ownedAddons.map((id) => {
                const item = PLAN_ADDONS.find((addon) => addon.id === id);
                return (
                  <li key={id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`billing-owned-addon-${id}`}>
                    <span className="text-muted-foreground">✓ {item?.name || id} (monthly)</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      data-testid={`billing-remove-addon-${id}`}
                      disabled={removingId === id}
                      onClick={() => removeMonthly(id)}
                    >
                      {removingId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      Remove
                    </Button>
                  </li>
                );
              })}
              {ownedOneTime.map((id) => {
                const item = PLAN_ONE_TIME.find((entry) => entry.id === id);
                return <li key={id} className="text-muted-foreground">✓ {item?.name || id} (purchased)</li>;
              })}
            </ul>
          )}
        </div>

        {!subscriptionActive ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="billing-addons-locked">
            Activate Foundation during onboarding first. After that you can buy Growth Pack, seats, AI credits, and more here.
          </p>
        ) : (
          <>
            <p className="mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Buy monthly add-ons</p>
            <div className="mt-3 space-y-3">
              {PLAN_ADDONS.filter((item) => !item.disabled).map((addon) => {
                const owned = ownedAddons.includes(addon.id);
                const selected = draftAddons.includes(addon.id);
                return (
                  <PlanAddonOffer
                    key={addon.id}
                    kind="monthly"
                    id={addon.id}
                    selected={owned || selected}
                    disabled={owned}
                    onToggle={() => {
                      if (owned) return;
                      setDraftAddons((current) => togglePlanSelection(current, addon.id));
                    }}
                    testId={`billing-addon-${addon.id}`}
                  />
                );
              })}
            </div>

            <p className="mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">One-time / prepaid</p>
            <div className="mt-3 space-y-3">
              {PLAN_ONE_TIME.map((item) => {
                const alreadyOwned = item.id === "custom_colors" && ownedOneTime.includes(item.id);
                const selected = draftOneTime.includes(item.id);
                return (
                  <PlanAddonOffer
                    key={item.id}
                    kind="one_time"
                    id={item.id}
                    selected={alreadyOwned || selected}
                    disabled={alreadyOwned}
                    onToggle={() => {
                      if (alreadyOwned) return;
                      setDraftOneTime((current) => togglePlanSelection(current, item.id));
                    }}
                    testId={`billing-one-time-${item.id}`}
                  />
                );
              })}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-[#F8F5EE] px-4 py-3" data-testid="billing-addons-due">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CreditCard className="h-4 w-4" />
                Due now ({dueLabel}) · excl. VAT
              </div>
              <p className="font-extrabold text-[#173B2F]">
                {formatMoney(dueNow, FOUNDATION_PLAN.currency)}
                <span className="ml-1 text-xs font-bold text-muted-foreground">+ VAT</span>
                {monthlyDelta > 0 ? (
                  <span className="ml-2 text-xs font-bold text-muted-foreground">
                    incl. {formatMoney(monthlyDelta, FOUNDATION_PLAN.currency)}/mo
                  </span>
                ) : null}
              </p>
            </div>

            {!stripeReady ? (
              <p className="mt-3 text-sm text-amber-800" data-testid="billing-stripe-missing">
                Payments are temporarily unavailable. Please try again shortly or contact support.
              </p>
            ) : null}

            <Button
              data-testid="billing-addons-purchase-button"
              className="tap-target mt-4 rounded-lg font-bold"
              disabled={paying || dueNow <= 0 || !stripeReady}
              onClick={purchase}
            >
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {paying ? "Processing…" : "Pay with Stripe"}
            </Button>
          </>
        )}
      </div>
  );

  if (embedded) {
    return (
      <div className={className} data-testid="billing-addons-panel" id="billing">
        <p className="mb-4 text-sm text-muted-foreground" data-testid="billing-addons-panel-intro">
          Update monthly add-ons or buy prepaid items anytime. Charges go through Stripe Checkout.
        </p>
        {body}
      </div>
    );
  }

  return (
    <section className={`rounded-lg border bg-white p-5 shadow-sm ${className}`} data-testid="billing-addons-panel" id="billing">
      <h3 className="text-lg font-extrabold" data-testid="billing-addons-panel-title">Plan, Growth Pack &amp; add-ons</h3>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="billing-addons-panel-intro">
        Update monthly add-ons or buy prepaid items anytime. Charges go through Stripe Checkout.
      </p>
      {body}
    </section>
  );
}
